/**
 * The Article 23 clocks.
 *
 * WHY THIS TICKS LOCALLY RATHER THAN POLLING
 * The deadline arithmetic lives in @opencomply/shared and the server returns
 * the inputs, not a pre-rendered countdown. So the browser recomputes once a
 * second from the same function the exports use. A polled countdown would be
 * one request per second per open tab, and it would freeze on a bad connection
 * — which is exactly when someone is staring at it.
 *
 * HOW A PASSED DEADLINE LOOKS, AND WHY
 * It does not disappear, grey out, or soften into "pending". It turns red, says
 * DEADLINE PASSED in words, states how long ago, and keeps the call to action
 * in place — because the obligation does not expire with the deadline. A missed
 * deadline is a fact the customer has to report on and account for; a product
 * that hid it would be helping them conceal it from their own regulator.
 *
 * Colour is never the only signal. Every state carries a text label and an
 * icon, so the screen works in greyscale and for a colour-blind reader.
 */

import { useEffect, useState, type ReactNode } from 'react';
import {
  formatDuration,
  type DeadlineView,
  type ScheduledStage,
} from '@opencomply/shared';
import { Pill, type Tone } from './ui';
import { cn } from '../lib/cn';
import { useIncidentT, type IncidentKey } from '../lib/incidentStrings';

/** One second. Fine-grained enough to be alive, cheap enough to ignore. */
const TICK_MS = 1000;

/** Re-renders every second so the countdown moves without a network request. */
export function useTick(enabled = true): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!enabled) return;
    const id = window.setInterval(() => setNow(Date.now()), TICK_MS);
    return () => window.clearInterval(id);
  }, [enabled]);
  return now;
}

/**
 * Recompute a deadline's live numbers against the current clock.
 *
 * The SERVER decides the state of anything already submitted — that is a fact
 * about the past and must not change as the page sits open. Only an
 * outstanding deadline is re-evaluated here, because only it moves.
 */
export function liveDeadline(view: DeadlineView, now: number): DeadlineView {
  if (!view.dueAt || view.submittedAt) return view;
  const msRemaining = new Date(view.dueAt).getTime() - now;
  const state: DeadlineView['state'] =
    msRemaining <= 0
      ? 'OVERDUE'
      : view.state === 'DUE_SOON' || view.state === 'OPEN'
        ? // The window is recovered from the original pair so "due soon" does
          // not depend on the server's idea of "now".
          msRemaining <= windowFor(view) * 0.25
          ? 'DUE_SOON'
          : 'OPEN'
        : view.state;
  return { ...view, msRemaining, state };
}

function windowFor(view: DeadlineView): number {
  if (view.stage === 'EARLY_WARNING') return 24 * 3_600_000;
  if (view.stage === 'NOTIFICATION') return 72 * 3_600_000;
  // A calendar month has no constant length; 30 days is only used to decide
  // when to start colouring it amber, never to compute the date itself.
  return 30 * 86_400_000;
}

export function toneForDeadline(state: DeadlineView['state']): Tone {
  switch (state) {
    case 'OVERDUE':
    case 'SUBMITTED_LATE':
      return 'critical';
    case 'DUE_SOON':
      return 'warning';
    case 'SUBMITTED_ON_TIME':
      return 'good';
    default:
      return 'neutral';
  }
}

function IconClock({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 20 20" className={className} fill="currentColor" aria-hidden>
      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm.75-13a.75.75 0 00-1.5 0v5c0 .28.16.54.4.67l3 1.5a.75.75 0 00.7-1.34l-2.6-1.3V5z" clipRule="evenodd" />
    </svg>
  );
}

function IconAlert({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 20 20" className={className} fill="currentColor" aria-hidden>
      <path fillRule="evenodd" d="M8.49 2.96c.64-1.1 2.23-1.1 2.87 0l6.14 10.52c.65 1.11-.16 2.52-1.44 2.52H3.79c-1.28 0-2.09-1.41-1.44-2.52L8.49 2.96zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
    </svg>
  );
}

function IconDone({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 20 20" className={className} fill="currentColor" aria-hidden>
      <path fillRule="evenodd" d="M16.7 5.3a1 1 0 010 1.4l-7.5 7.5a1 1 0 01-1.4 0l-3.5-3.5a1 1 0 111.4-1.4l2.8 2.79 6.8-6.79a1 1 0 011.4 0z" clipRule="evenodd" />
    </svg>
  );
}

/**
 * One deadline, stated honestly.
 *
 * Every branch produces a heading, a status word and a sentence of explanation.
 * The explanation is not a tooltip: at 2am nobody hovers.
 */
export function DeadlineRow({
  view,
  now,
  action,
}: {
  view: DeadlineView;
  now: number;
  action?: ReactNode;
}) {
  const { tx, formatDateTime } = useIncidentT();
  const live = liveDeadline(view, now);
  const tone = toneForDeadline(live.state);

  const stageName = tx(`incident.stage.${live.stage}` as IncidentKey);
  const cite = tx(`incident.stageCite.${live.stage}` as IncidentKey);
  const limit = tx(`incident.stageLimit.${live.stage}` as IncidentKey);

  const Icon =
    live.state === 'OVERDUE' || live.state === 'SUBMITTED_LATE'
      ? IconAlert
      : live.state === 'SUBMITTED_ON_TIME'
        ? IconDone
        : IconClock;

  let statusLabel: string;
  let detail: string | null = null;

  switch (live.state) {
    case 'OVERDUE':
      statusLabel = tx('incident.deadline.overdue');
      detail = tx('incident.deadline.overdueDetail', {
        when: formatDateTime(live.dueAt),
        duration: formatDuration(live.msRemaining ?? 0),
      });
      break;
    case 'SUBMITTED_LATE':
      statusLabel = tx('incident.deadline.submittedLate', {
        duration: formatDuration(live.msLateBy ?? 0),
      });
      detail = tx('incident.deadline.submittedLateDetail', {
        when: formatDateTime(live.submittedAt),
        duration: formatDuration(live.msLateBy ?? 0),
      });
      break;
    case 'SUBMITTED_ON_TIME':
      statusLabel = tx('incident.deadline.submittedOnTime');
      detail = `${tx('incident.deadline.dueAt', { when: formatDateTime(live.dueAt) })} · ${formatDateTime(live.submittedAt)}`;
      break;
    case 'NOT_YET_SCHEDULED':
      statusLabel = tx('incident.deadline.notScheduled');
      detail = tx('incident.deadline.notScheduledDetail');
      break;
    default:
      statusLabel = tx('incident.deadline.remaining', {
        duration: formatDuration(live.msRemaining ?? 0),
      });
      detail = tx('incident.deadline.dueAt', { when: formatDateTime(live.dueAt) })
        + (live.stage === 'FINAL' ? ` · ${tx('incident.deadline.monthNote')}` : '');
  }

  return (
    <div
      className={cn(
        'rounded-xl border p-3 sm:p-4 flex flex-col gap-2',
        // A left bar carries the urgency in a second, non-colour channel too:
        // its presence and thickness differ, not only its hue.
        live.state === 'OVERDUE' || live.state === 'SUBMITTED_LATE'
          ? 'border-critical/40 bg-critical/[0.06] border-l-4 border-l-critical'
          : live.state === 'DUE_SOON'
            ? 'border-warning/40 bg-warning/[0.06] border-l-4 border-l-warning'
            : 'border-line bg-surface',
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Icon
              className={cn(
                'w-4 h-4 shrink-0',
                tone === 'critical' && 'text-critical',
                tone === 'warning' && 'text-warning',
                tone === 'good' && 'text-good',
                tone === 'neutral' && 'text-ink-muted',
              )}
            />
            <span className="font-semibold text-ink">{stageName}</span>
            <span className="text-[11px] text-ink-muted font-mono">{cite}</span>
          </div>
          <p className="text-xs text-ink-muted mt-0.5">{limit}</p>
        </div>
        <Pill tone={tone}>{statusLabel}</Pill>
      </div>

      {detail && (
        <p
          className={cn(
            'text-xs leading-relaxed',
            live.state === 'OVERDUE' || live.state === 'SUBMITTED_LATE'
              ? 'text-critical font-medium'
              : 'text-ink-secondary',
          )}
        >
          {detail}
        </p>
      )}

      {action && <div className="flex flex-wrap gap-2 pt-0.5">{action}</div>}
    </div>
  );
}

/**
 * A compact countdown for the register, where twenty incidents share a screen.
 *
 * Shows only the most urgent clock, because a row that lists three deadlines is
 * a row nobody reads. The full set is one click away.
 */
export function DeadlineChip({ deadlines, now }: { deadlines: DeadlineView[]; now: number }) {
  const { tx } = useIncidentT();
  const live = deadlines.map((d) => liveDeadline(d, now));

  const overdue = live.find((d) => d.state === 'OVERDUE');
  const late = live.find((d) => d.state === 'SUBMITTED_LATE');
  const soonest = live
    .filter((d) => d.state === 'DUE_SOON' || d.state === 'OPEN')
    .sort((a, b) => (a.msRemaining ?? 0) - (b.msRemaining ?? 0))[0];

  if (overdue) {
    return (
      <Pill tone="critical">
        {tx(`incident.stage.${overdue.stage as ScheduledStage}` as IncidentKey)} ·{' '}
        {tx('incident.deadline.overdue')}
      </Pill>
    );
  }
  if (soonest) {
    return (
      <Pill tone={soonest.state === 'DUE_SOON' ? 'warning' : 'neutral'}>
        {tx(`incident.stage.${soonest.stage as ScheduledStage}` as IncidentKey)} ·{' '}
        {tx('incident.deadline.remaining', { duration: formatDuration(soonest.msRemaining ?? 0) })}
      </Pill>
    );
  }
  if (late) return <Pill tone="critical">{tx('incident.missedDeadline')}</Pill>;
  return <Pill tone="good">{tx('incident.nextAction.none')}</Pill>;
}
